// Settings: engine audio, volume, reduced motion; the global board (nickname, submission on/off, delete my
// online runs); reset progress (confirmed); build version.
import type { AppContext, ScreenHandle } from './app';
import type { SaveSettings } from '../types';
import { resetSave, sanitizeNickname, NICKNAME_MAX } from '../save';
import { deleteMyRuns } from '../net/board';
import { isConfigured } from '../net/supabase';
import { h, clear, button, confirmDialog } from './dom';
import { fmtCredits } from './format';

export function mountSettings(root: HTMLElement, ctx: AppContext): ScreenHandle {
  const panel = h('div.panel');
  const online = h('div.panel');
  const about = h('div.panel');
  root.append(
    h('div.wrap', {},
      h('header.topbar', {},
        button('← Hangar', () => ctx.go('hangar'), { cls: 'btn btn-quiet', ariaLabel: 'Back to the hangar' }),
        h('div.title', {}, h('h1', { id: 'settings-title' }, 'Settings'), h('p', {}, 'Stored in this browser. Only completed runs leave it, to the global board, and only while that is switched on.')),
      ),
      panel, online, about,
    ),
  );

  function save(patch: Partial<SaveSettings>): void {
    ctx.save.settings = { ...ctx.save.settings, ...patch };
    ctx.persist();
    ctx.applySettings();
    render();
  }

  function renderSound(s: SaveSettings): void {
    clear(panel);
    panel.append(h('h2', {}, 'Sound and motion'));

    const audio = h('input', { type: 'checkbox', id: 'set-audio' });
    audio.checked = s.audio;
    audio.addEventListener('change', () => save({ audio: audio.checked }));
    panel.append(h('div.field', {}, h('label.switch', { for: 'set-audio' }, audio, 'Engine audio (real recordings)')));

    const volOut = h('span.cell-value', {}, `${Math.round(s.volume * 100)} %`);
    const vol = h('input', { type: 'range', id: 'set-volume', min: '0', max: '100', step: '5', value: String(Math.round(s.volume * 100)), disabled: !s.audio });
    vol.addEventListener('input', () => { volOut.textContent = `${vol.value} %`; });
    vol.addEventListener('change', () => save({ volume: Number(vol.value) / 100 }));
    panel.append(h('div.field', {}, h('label', { for: 'set-volume' }, 'Volume: ', volOut), vol));

    const seg = h('div.seg', { role: 'group', 'aria-label': 'Reduced motion' });
    for (const [value, label] of [['system', 'System'], ['on', 'On'], ['off', 'Off']] as const) {
      const b = button(label, () => save({ reducedMotion: value }), { cls: 'btn', ariaLabel: `Reduced motion: ${label}` });
      b.setAttribute('aria-pressed', String(s.reducedMotion === value));
      seg.append(b);
    }
    panel.append(h('div.field', {}, h('span.label', {}, 'Reduced motion'), seg, h('p.hint', {}, `Currently ${ctx.reducedMotion() ? 'reduced: the ocean holds still and screens do not fade' : 'full motion'}.`)));
  }

  function renderBoard(s: SaveSettings): void {
    clear(online);
    online.append(h('h2', {}, 'Global board'));

    const nick = h('input', { type: 'text', id: 'set-nickname', maxlength: NICKNAME_MAX, autocomplete: 'nickname', autocapitalize: 'off', spellcheck: 'false' });
    nick.value = s.nickname;
    nick.addEventListener('change', () => {
      // Empty or whitespace keeps the previous name; the field shows what was actually saved.
      const next = sanitizeNickname(nick.value, s.nickname);
      nick.value = next;
      if (next !== s.nickname) save({ nickname: next });
    });
    online.append(h('div.field', {},
      h('label', { for: 'set-nickname' }, 'Nickname on the board'),
      nick,
      h('p.hint', {}, `1–${NICKNAME_MAX} characters. Shown next to your runs from the next submission on.`),
    ));

    const submit = h('input', { type: 'checkbox', id: 'set-online' });
    submit.checked = s.online;
    submit.addEventListener('change', () => {
      // Switching off also discards runs still waiting to be sent: "do not submit" means these too.
      if (!submit.checked) ctx.save.pending = [];
      save({ online: submit.checked });
    });
    online.append(
      h('div.field', {}, h('label.switch', { for: 'set-online' }, submit, 'Submit runs to the global board')),
      h('p.hint', {}, 'Runs are submitted under an anonymous account ID with your nickname: craft, route day, distance, fuel used, simulated time, efficiency index, installed mods and the app version. Nothing else leaves this device.'),
    );

    const del = button('Delete my online runs', () => void deleteOnline(), { cls: 'btn btn-danger', disabled: !isConfigured });
    online.append(h('div.field', {}, del, isConfigured ? null : h('p.hint', {}, 'The global board is not available in this build.')));
  }

  function render(): void {
    const s = ctx.save.settings;
    renderSound(s);
    renderBoard(s);
    clear(about);
    about.append(
      h('h2', {}, 'Progress'),
      h('p.muted', { style: 'margin:.5rem 0 1rem' }, `${fmtCredits(ctx.save.credits)} · ${ctx.save.owned.length} craft owned · ${ctx.save.runs.length} run${ctx.save.runs.length === 1 ? '' : 's'} kept`),
      button('Reset progress', () => void reset(), { cls: 'btn btn-danger' }),
      h('p.small.muted', { style: 'margin-top:1.25rem' }, `SkyBattleSea ${ctx.build}`),
    );
  }

  async function deleteOnline(): Promise<void> {
    const ok = await confirmDialog(
      'Delete your online runs?',
      'Every run submitted from this browser is removed from the global board, and runs still waiting to be sent are discarded. Runs kept on this device stay. This cannot be undone.',
      'Delete',
    );
    if (!ok) return;
    ctx.save.pending = [];
    ctx.persist();
    const n = await deleteMyRuns();
    if (n === null) ctx.toast('The global board could not be reached. Try again later.');
    else ctx.toast(n === 0 ? 'No online runs to delete.' : `${n} online run${n === 1 ? '' : 's'} deleted.`);
  }

  async function reset(): Promise<void> {
    const ok = await confirmDialog('Reset all progress?', 'Credits, purchases, builds, bests and the run log are deleted from this browser. This cannot be undone.', 'Reset');
    if (!ok) return;
    ctx.save = resetSave();
    ctx.applySettings();
    ctx.toast('Progress reset.');
    ctx.go('hangar');
  }

  return { show: render };
}
